import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Wallet } from "lucide-react";
import { api } from "@/lib/api";
import { money } from "@/lib/rides";
import { SectionTitle } from "@/components/rider/Nav";

const DAYS = ["M", "T", "W", "T", "F", "S", "S"];
const fmt = (iso) => new Date(iso).toLocaleDateString([], { month: "short", day: "numeric", timeZone: "America/Chicago" });

function Bars({ days }) {
  const max = Math.max(...days, 1);
  return (
    <div className="mt-3 flex h-16 items-end gap-1.5" data-testid="earnings-bars">
      {days.map((v, i) => (
        <div key={i} className="flex flex-1 flex-col items-center gap-1">
          <div className="w-full rounded-t bg-primary/80 transition-all" style={{ height: `${Math.max(4, (v / max) * 48)}px`, opacity: v ? 1 : 0.2 }} title={money(v)} />
          <span className="text-[10px] text-muted-foreground">{DAYS[i]}</span>
        </div>
      ))}
    </div>
  );
}

export function WeeklyEarnings() {
  const [ago, setAgo] = useState(0);
  const [open, setOpen] = useState(false);
  const { data } = useQuery({ queryKey: ["driverEarnings", ago], queryFn: () => api.get(`/driver/earnings?weeks_ago=${ago}`).then((r) => r.data) });
  return (
    <section className="space-y-2" data-testid="weekly-earnings">
      <SectionTitle><span className="flex items-center gap-1.5"><Wallet className="h-3 w-3" />Weekly earnings</span></SectionTitle>
      <div className="rounded-2xl border border-white/10 bg-card p-4">
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setAgo(ago + 1)} disabled={ago >= 52} className="rounded-full p-1 hover:bg-muted disabled:opacity-30" data-testid="earnings-prev-week"><ChevronLeft className="h-4 w-4" /></button>
          <span className="flex-1 text-center text-xs text-muted-foreground" data-testid="earnings-week-label">
            {data ? `${ago === 0 ? "This week · " : ""}${fmt(data.week_start)} – ${fmt(data.week_end)}` : "Loading…"}
          </span>
          <button type="button" onClick={() => setAgo(ago - 1)} disabled={ago === 0} className="rounded-full p-1 hover:bg-muted disabled:opacity-30" data-testid="earnings-next-week"><ChevronRight className="h-4 w-4" /></button>
        </div>
        <div className="mt-2 flex items-end justify-between">
          <span className="font-display text-3xl font-bold text-primary" data-testid="earnings-total">{money(data?.total ?? 0)}</span>
          <span className="text-xs text-muted-foreground" data-testid="earnings-count">{data?.count ?? 0} completed ride{data?.count === 1 ? "" : "s"}</span>
        </div>
        {data && <Bars days={data.days} />}
        {!!data?.rides.length && (
          <button type="button" onClick={() => setOpen(!open)} className="mt-3 text-xs font-semibold text-primary" data-testid="earnings-toggle-rides">{open ? "Hide rides" : "Show rides"}</button>
        )}
        {open && data?.rides.map((r) => (
          <div key={r.id} className="mt-2 flex items-center gap-2 border-t border-white/5 pt-2 text-xs" data-testid={`earnings-ride-${r.id}`}>
            <span className="min-w-0 flex-1"><span className="block truncate font-semibold">{r.pickup} → {r.dropoff}</span>
              <span className="text-muted-foreground">{r.rider_name} · {new Date(r.completed_at).toLocaleString([], { weekday: "short", hour: "numeric", minute: "2-digit", timeZone: "America/Chicago" })}</span></span>
            <span className="font-display font-bold">{money(r.fare)}</span>
          </div>
        ))}
      </div>
    </section>
  );
}
